import { db } from "./firebase";
import { doc, getDoc } from "firebase/firestore";

export interface CloudinaryConfig {
  cloudName: string;
  uploadPreset: string;
}

export async function getCloudinaryConfig(): Promise<CloudinaryConfig> {
  try {
    const docRef = doc(db, "configs", "generalSettings");
    const snap = await getDoc(docRef);
    if (snap.exists()) {
      const data = snap.data();
      if (data.cloudinaryCloudName) {
        return {
          cloudName: data.cloudinaryCloudName,
          uploadPreset: data.cloudinaryUploadPreset || "ml_default",
        };
      }
    }
  } catch (err) {
    console.error("Failed to load Cloudinary config from Firestore:", err);
  }

  // Fallback to env variables if Firestore load fails or is empty
  return {
    cloudName: (import.meta.env.VITE_CLOUDINARY_CLOUD_NAME as string) || "rq5zcg8i",
    uploadPreset: (import.meta.env.VITE_CLOUDINARY_UPLOAD_PRESET as string) || "ml_default",
  };
}

/**
 * Compresses an image file client-side before upload to speed up transmission and reduce bandwidth.
 */
export async function compressImage(file: File, maxW = 1600, maxH = 1600, quality = 0.8): Promise<File> {
  return new Promise((resolve) => {
    // Only compress typical web images
    if (!file.type.startsWith("image/") || file.type === "image/gif" || file.type === "image/svg+xml") {
      return resolve(file);
    }

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;
      img.onload = () => {
        let width = img.width;
        let height = img.height;

        // Resize logic to preserve aspect ratio within max dimensions
        if (width > maxW || height > maxH) {
          if (width > height) {
            height = Math.round((height * maxW) / width);
            width = maxW;
          } else {
            width = Math.round((width * maxH) / height);
            height = maxH;
          }
        }

        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext("2d");
        if (!ctx) return resolve(file);

        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              const compressedFile = new File([blob], file.name, {
                type: "image/jpeg",
                lastModified: Date.now(),
              });
              resolve(compressedFile);
            } else {
              resolve(file);
            }
          },
          "image/jpeg",
          quality
        );
      };
      img.onerror = () => resolve(file);
    };
    reader.onerror = () => resolve(file);
  });
}

export async function uploadToCloudinary(file: File): Promise<string> {
  const config = await getCloudinaryConfig();

  if (!config.cloudName) {
    throw new Error("Cloudinary is not configured. Please check VITE_CLOUDINARY_CLOUD_NAME.");
  }

  // Compress image client-side to speed up upload
  const compressedFile = await compressImage(file);

  const formData = new FormData();
  formData.append("file", compressedFile);
  formData.append("upload_preset", config.uploadPreset);

  const res = await fetch(`https://api.cloudinary.com/v1_1/${config.cloudName}/image/upload`, {
    method: "POST",
    body: formData,
  });

  if (!res.ok) {
    const responseText = await res.text();
    let message = res.statusText;
    try {
      const parsed = JSON.parse(responseText);
      if (parsed?.error?.message) {
        message = parsed.error.message;
      }
    } catch {
      message = responseText || res.statusText;
    }
    throw new Error(`Cloudinary upload failed: ${message}`);
  }

  const data = (await res.json()) as { secure_url: string };
  return data.secure_url;
}
